import React, { useState, useEffect, useRef } from 'react';
import { LuBell, LuSearch, LuUser, LuMenu, LuSettings, LuLogOut, LuX, LuCheck, LuClock, } from 'react-icons/lu';
import { useAuth } from '../../providers/auth.provider';
import { Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { BiPhone } from 'react-icons/bi';
import { FiMail } from 'react-icons/fi';

interface NavbarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  setIsMobileOpen: (value: boolean) => void;
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const sampleNotifications: Notification[] = [
  {
    id: '1',
    type: 'success',
    title: 'Project Completed',
    message: 'Website redesign project has been successfully completed.',
    time: '2 hours ago',
    read: false
  },
  {
    id: '2',
    type: 'info',
    title: 'New Message',
    message: 'You have received a new message from Sarah.',
    time: '3 hours ago',
    read: false
  },
  {
    id: '3',
    type: 'warning',
    title: 'Storage Alert',
    message: 'Your storage is almost full. Please free up some space.',
    time: '5 hours ago',
    read: false
  },
  {
    id: '4',
    type: 'info',
    title: 'Meeting Reminder',
    message: 'Team meeting starts in 30 minutes.',
    time: '1 day ago',
    read: true
  }
];

const Navbar = ({ isCollapsed, isMobileOpen, setIsMobileOpen }: NavbarProps) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(sampleNotifications);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const { logout, userData, partnerData } = useAuth();
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <LuCheck className="w-4 h-4 text-green-500" />;
      case 'warning':
        return <LuCheck className="w-4 h-4 text-yellow-500" />;
      default:
        return <LuClock className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <nav className={`
      fixed top-0 right-0 z-10
      ${isCollapsed ? 'lg:left-20' : 'lg:left-72'}
      left-0
      bg-white border-b border-gray-200
      transition-all duration-300
    `}>
      <div className="px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          >
            <LuMenu className="w-5 h-5 text-gray-600" />
          </button>
          {/* <div className="relative hidden sm:block">
            <LuSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 w-72"
            />
          </div> */}
        </div>

        <div className="flex items-center gap-4">

          <div className="flex items-center space-x-6">
            <Link
              to={`mailto:${partnerData?.company_email ? partnerData?.company_email : 'help@larkfinserv.com'}?subject=Enquiry%20for%20Loan%20Against%20Mutual%20Fund&body=Hi%20Team%2C%0D%0A%0D%0AI%20am%20interested%20in%20Loan%20Against%20Mutual%20Fund.%0D%0A%0D%0ALet%20me%20know%20if%20any%20thing%20needed.%0D%0A%0D%0AThanks!%0D%0A%0D%0A`}
              className="text-blue-800 hover:text-gray-900 flex items-center gap-1"
            >
              <FiMail className="" /> <span>Need help?</span>
            </Link>
            <Link to={`tel:+91${partnerData?.company_contact_no ? partnerData?.company_contact_no : '7836005500'}`} className="flex gap-1 justify-center items-center text-gray-700 hover:text-gray-900 sm:hidden md:flex">
              <BiPhone className="text-blue-500 w-6 h-6" />
              <span className="hidden sm:block">+91 {partnerData?.company_contact_no ? partnerData?.company_contact_no : '7836005500'}</span>
            </Link>
            <Link
              to={`https://wa.me/${partnerData?.company_wa_no ? partnerData?.company_wa_no : '7836009900'}?text=I%20am%20interested%20in%20Loan%20Against%20Mutual%20Fund.%0D%0ALet%20me%20know%20if%20any%20thing%20needed.%0D%0A%0D%0AThanks!`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 sm:hidden md:flex"
            >
              <FaWhatsapp className="text-green-500 w-6 h-6" />
              <span className="hidden sm:block">Chat With Us</span>
            </Link>
          </div>

          <div className="relative" ref={notificationRef}>
            {/* <button
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative p-2 hover:bg-gray-100 rounded-lg"
            >
              <LuBell className="w-5 h-5 text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button> */}

            {/* Notifications Dropdown */}
            {isNotificationOpen && (
              <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-10">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No notifications
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`
                          p-4 border-b border-gray-100 last:border-0
                          ${notification.read ? 'bg-white' : 'bg-blue-50'}
                          hover:bg-gray-50 transition-colors
                          relative group
                        `}
                      >
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-0.5">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {notification.time}
                            </p>
                          </div>
                          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1 hover:bg-gray-200 rounded"
                                title="Mark as read"
                              >
                                <LuCheck className="w-4 h-4 text-gray-600" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1 hover:bg-gray-200 rounded"
                              title="Delete"
                            >
                              <LuX className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <Link
                    to="/notifications"
                    className="block text-center text-sm text-blue-600 hover:text-blue-700"
                  >
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 border-l pl-4 hover:bg-gray-50 rounded-lg p-1 transition-colors"
            >
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-medium">{userData?.first_name}</span>
                {/* <span className="text-xs text-gray-500">{userData?.role?.name}</span> */}
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <LuUser className="w-5 h-5 text-gray-600" />
              </div>
            </button>

            {/* LuUser Dropdown LuMenu */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">{userData?.first_name}</p>
                  <p className="text-sm text-gray-500">{userData?.email}</p>
                </div>

                <div className="">
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 p-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LuUser className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                </div>

                {/* <div className="border-t border-gray-100">
                  <Link
                    to="/settings"
                    className="flex items-center gap-3 p-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LuSettings className="w-4 h-4" />
                    <span>Settings</span>
                  </Link>
                </div> */}

                <div className="border-t border-gray-100">
                  <button
                    onClick={logout}
                    className="flex items-center gap-3 p-4 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
                  >
                    <LuLogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;